import type { ReactNode } from 'react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Button } from '@/src/elements/button/default'
import { Stack } from '@/src/elements/navigation/Stack'
import { GENERAL_NOTIFICATION_SETTINGS } from '@/src/features/notification/models/notification.constants'
import { WatchmanLeaveModal } from '@/src/features/watchman/ui/components/WatchmanLeaveModal'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { useTranslate } from '@/src/lang/useTranslate'

import { MultipleSettingsCardGeneral } from '../components/MultipleSettingsCardGeneral/MultipleSettingsCardGeneral'
import { SettingsCardGeneral } from '../components/SettingsCardGeneral'
import { SettingsScreenWrapper } from '../components/SettingsScreenWrapper'

export const NotificationsGeneralScreen = ({ searchParams }: TSearchParamsParam): ReactNode => {
  const [showLeaveWatchmanModal, setShowLeaveWatchmanModal] = useState(false)

  const { t } = useTranslate()
  const { updateParametersToken, setting } = filterSearchParams<{
    updateParametersToken: string
    setting: GENERAL_NOTIFICATION_SETTINGS
  }>(searchParams)

  const isValidSetting = Object.values(GENERAL_NOTIFICATION_SETTINGS).includes(setting)

  return (
    <SettingsScreenWrapper testID={TEST_ID.SETTINGS.GENERAL.SCREEN}>
      <Stack.Screen options={{ title: isValidSetting ? undefined : t('settings.notifications.general.title') }} />
      {!isValidSetting && (
        <>
          <MultipleSettingsCardGeneral
            type="email"
            testID={TEST_ID.SETTINGS.GENERAL.ALL_EMAILS}
            updateParametersToken={updateParametersToken}
            label={t('settings.email.disable.all')}
          />
          <MultipleSettingsCardGeneral
            type="push"
            testID={TEST_ID.SETTINGS.GENERAL.ALL_PUSH}
            updateParametersToken={updateParametersToken}
            label={t('settings.notifications.disable.all')}
          />
        </>
      )}
      {(setting === GENERAL_NOTIFICATION_SETTINGS.NEWSLETTER || !isValidSetting) && (
        <SettingsCardGeneral
          updateParametersToken={updateParametersToken}
          settingsKey={GENERAL_NOTIFICATION_SETTINGS.NEWSLETTER}
          testID={TEST_ID.SETTINGS.GENERAL.NEWSLETTER}
          title={t('settings.notifications.newsletter.title')}
        />
      )}
      {(setting === GENERAL_NOTIFICATION_SETTINGS.DIGEST || !isValidSetting) && (
        <SettingsCardGeneral
          updateParametersToken={updateParametersToken}
          settingsKey={GENERAL_NOTIFICATION_SETTINGS.DIGEST}
          testID={TEST_ID.SETTINGS.GENERAL.DIGEST}
          title={t('settings.notifications.pray-remind.title')}
          withPush
        />
      )}
      {(setting === GENERAL_NOTIFICATION_SETTINGS.ON_INTENTION_FINISHED || !isValidSetting) && (
        <SettingsCardGeneral
          updateParametersToken={updateParametersToken}
          settingsKey={GENERAL_NOTIFICATION_SETTINGS.ON_INTENTION_FINISHED}
          testID={TEST_ID.SETTINGS.GENERAL.ON_INTENTION_FINISHED}
          title={t('settings.notifications.intention-end-time.title')}
          withPush
        />
      )}
      {setting === GENERAL_NOTIFICATION_SETTINGS.ON_INTENTION_WATCHMAN && isValidSetting && (
        <>
          <SettingsCardGeneral
            updateParametersToken={updateParametersToken}
            settingsKey={GENERAL_NOTIFICATION_SETTINGS.ON_INTENTION_WATCHMAN}
            testID={TEST_ID.SETTINGS.GENERAL.ON_INTENTION_WATCHMAN}
            title={t('settings.notifications.watchman-remind.title')}
            withPush
          />
          <Button
            colors="danger"
            variant="light"
            startIcon={ICON.LEAVE}
            className="m-4"
            onPress={() => setShowLeaveWatchmanModal(true)}
          >
            <FormattedMessage id="intention.prayer.watchman.stop" />
          </Button>
          <WatchmanLeaveModal isOpen={showLeaveWatchmanModal} onClose={() => setShowLeaveWatchmanModal(false)} />
        </>
      )}
      {(setting === GENERAL_NOTIFICATION_SETTINGS.ON_PRIVATE_MESSAGE || !isValidSetting) && (
        <SettingsCardGeneral
          updateParametersToken={updateParametersToken}
          settingsKey={GENERAL_NOTIFICATION_SETTINGS.ON_PRIVATE_MESSAGE}
          testID={TEST_ID.SETTINGS.GENERAL.ON_PRIVATE_MESSAGE}
          title={t('settings.notifications.message.title')}
          withPush
        />
      )}
      {(setting === GENERAL_NOTIFICATION_SETTINGS.ON_GODSON_JOINED || !isValidSetting) && (
        <SettingsCardGeneral
          updateParametersToken={updateParametersToken}
          settingsKey={GENERAL_NOTIFICATION_SETTINGS.ON_GODSON_JOINED}
          testID={TEST_ID.SETTINGS.GENERAL.ON_GODSON_JOINED}
          title={t('settings.notifications.invited-join.title')}
        />
      )}
      {(setting === GENERAL_NOTIFICATION_SETTINGS.ON_COMMUNITY_JOINED || !isValidSetting) && (
        <SettingsCardGeneral
          updateParametersToken={updateParametersToken}
          settingsKey={GENERAL_NOTIFICATION_SETTINGS.ON_COMMUNITY_JOINED}
          testID={TEST_ID.SETTINGS.GENERAL.ON_COMMUNITY_JOINED}
          title={t('settings.notifications.community-registration.title')}
        />
      )}
      {(setting === GENERAL_NOTIFICATION_SETTINGS.ON_RETREAT_ENDED || !isValidSetting) && (
        <SettingsCardGeneral
          updateParametersToken={updateParametersToken}
          settingsKey={GENERAL_NOTIFICATION_SETTINGS.ON_RETREAT_ENDED}
          testID={TEST_ID.SETTINGS.GENERAL.ON_RETREAT_ENDED}
          title={t('settings.notifications.end-of-novena.title')}
          withPush
        />
      )}
      {(setting === GENERAL_NOTIFICATION_SETTINGS.ON_USER_CATCHUP || !isValidSetting) && (
        <SettingsCardGeneral
          updateParametersToken={updateParametersToken}
          settingsKey={GENERAL_NOTIFICATION_SETTINGS.ON_USER_CATCHUP}
          testID={TEST_ID.SETTINGS.GENERAL.ON_USER_CATCHUP}
          title={t('settings.notifications.no-ongoing-community.title')}
          withPush
        />
      )}
    </SettingsScreenWrapper>
  )
}
